import React, { useEffect, useRef } from 'react';
import { Network } from 'vis-network';

const ViewCanvas = ({ canvas, currentSituationId, isMinimapExpanded }) => {
    const networkRef = useRef(null);
    const networkInstanceRef = useRef(null);
    const optionsRef = useRef(null);

    if (!optionsRef.current) {
        optionsRef.current = {
            nodes: {
                shape: 'box',
                margin: 10,
                widthConstraint: {
                    maximum: 200,
                },
                font: {
                    size: 14,
                    face: 'arial',
                },
                color: {},
            },
            edges: {
                arrows: 'to',
                smooth: {
                    type: 'dynamic',
                    roundness: 0.5,
                },
                font: {
                    size: 12,
                    color: '#343434',
                    strokeWidth: 0,
                    align: 'horizontal',
                },
            },
            interaction: {
                dragNodes: false,
                dragView: true,
                zoomView: true,
            },
            layout: {
                improvedLayout: true,
            },
            physics: {
                enabled: true,
                solver: 'forceAtlas2Based',
                forceAtlas2Based: {
                    gravitationalConstant: -50,
                    centralGravity: 0.01,
                    springLength: 200,
                    springConstant: 0.08,
                },
                maxVelocity: 50,
                minVelocity: 0.75,
                stabilization: {
                    iterations: 1000,
                    updateInterval: 25,
                    onlyDynamicEdges: false,
                    fit: true,
                },
            },
        };
    }

    useEffect(() => {
        if (!canvas || !canvas.nodes || !canvas.edges || !networkRef.current) {
            if (networkInstanceRef.current) {
                try {
                    networkInstanceRef.current.destroy();
                } catch (e) {
                    console.warn("Error destroying stale network instance on canvas invalidation:", e);
                }
                networkInstanceRef.current = null;
            }
            return;
        }

        if (networkInstanceRef.current) {
            try {
                networkInstanceRef.current.destroy();
            } catch (e) {
                console.warn("Error destroying old network instance before re-initialization:", e);
            }
            networkInstanceRef.current = null;
        }

        const visNodes = canvas.nodes.map(node => ({
            id: node.id,
            label: node.data?.title || node.title || node.id,
        }));

        const visEdges = canvas.edges.map(edge => {
            let truncatedLabel = undefined;
            const edgeLabel = edge.data?.title;
            if (edgeLabel) {
                if (edgeLabel.length > 15) {
                    truncatedLabel = edgeLabel.substring(0, 15) + '...';
                } else {
                    truncatedLabel = edgeLabel;
                }
            }
            const edgeConfig = {
                id: edge.id,
                from: edge.source,
                to: edge.target,
                label: truncatedLabel,
            };
            if (edge.source === edge.target) {
                edgeConfig.selfReference = {
                    size: 20,
                    angle: Math.PI / 4,
                };
            }
            return edgeConfig;
        });

        const data = { nodes: visNodes, edges: visEdges };

        let network;
        try {
            network = new Network(networkRef.current, data, optionsRef.current);
            networkInstanceRef.current = network;
        } catch(e) {
            console.error("Failed to initialize Vis Network:", e);
            return;
        }

        const stabilizationCallback = () => {
            if (networkInstanceRef.current === network && network) {
                network.setOptions({ physics: false });
            }
        };
        network.on('stabilizationIterationsDone', stabilizationCallback);

        return () => {
            if (network && typeof network.destroy === 'function') {
                try {
                    network.off('stabilizationIterationsDone', stabilizationCallback);
                    network.destroy();
                } catch (e) {
                    console.warn("Error destroying network instance on cleanup:", e);
                }
            }
            if (networkInstanceRef.current === network) {
                networkInstanceRef.current = null;
            }
        };
    }, [canvas]);

    useEffect(() => {
        const network = networkInstanceRef.current;

        if (!network || !currentSituationId || !canvas || !canvas.nodes) {
            return;
        }

        if (!network.body || !network.body.data || !network.body.data.nodes || typeof network.body.data.nodes.update !== 'function') {
            return;
        }

        try {
            const nodeUpdates = canvas.nodes.map(node => ({
                id: node.id,
                color: node.id === currentSituationId ?
                    { background: 'yellow', border: '#FFD700' } :
                    optionsRef.current.nodes.color,
            }));

            network.body.data.nodes.update(nodeUpdates);

            const timerId = setTimeout(() => {
                const currentNetwork = networkInstanceRef.current;
                if (currentNetwork === network &&
                    currentNetwork.body?.data?.nodes?.get(currentSituationId)) {

                    currentNetwork.fit({
                        nodes: [currentSituationId],
                        animation: false
                    });
                    currentNetwork.focus(currentSituationId, {
                        scale: isMinimapExpanded ? 1.0 : 0.35,
                        animation: false
                    });
                }
            }, 50);

            return () => clearTimeout(timerId);

        } catch (error) {
            console.error("Error during network dynamic update (colors, fit, focus):", error);
        }
    }, [currentSituationId, isMinimapExpanded, canvas?.nodes]);

    return <div ref={networkRef} style={{ height: '100%', width: '100%' }} />;
};

export default ViewCanvas;